<template>
  <div :class="style.heading">
    <InputStyle
      description="Заголовок"
      placeholder="Введите заголовок"
      size="M"
      :modelValue="actionHeader"
      @update:modelValue="updateActionHeader"
    />
  </div>

  <AccordionBase
    text="Карточки"
    :showChevronWithActions="true"
    :classHeader="[style.accordionHeader]"
  >
    <template #actions>
      <div :class="style.actionAccordion">
        <ButtonIcon
          :icon="addIcon"
          @click.stop="addAction"
          variant="ghost"
          size="28"
        />
      </div>
    </template>

    <div :class="style.actionContainer">
      <InputFieldMedia
        v-for="(action, actionIndex) in actionList"
        :key="getActionKey(action, actionIndex)"
        placeholder="Введите текст"
        :image="getActionImage(action.img ?? '', actionIndex)"
        :mediaType="getActionMediaType(action.img ?? '')"
        :hasImage="!mobileVersion"
        :actions="getActionActions(actionIndex)"
        v-model="action.header"
        @upload="uploadPhotoAction(actionIndex)"
      />

      <input
        ref="fileUpload"
        type="file"
        hidden
        accept="image/*, video/*"
        @change="setActionImage"
      />
    </div>
  </AccordionBase>
</template>

<script lang="ts">
import {defineComponent, markRaw} from "vue";
import style from "./ActionSettingComponent.module.scss";
import {DOMAIN} from "@/http";
import {modifyQuiz} from "@/page/quiz/scriptQuiz";
import {action, Quiz} from "@/page/quiz/typeQuiz";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import AccordionBase from "@/elements/UI/accordion/AccordionBase.vue";
import InputFieldMedia from "@/elements/UI/input/Media/InputFieldMedia.vue";

import chevronUp from "$images/icons/button/Name=Chevron-up, Size=16, Tone=Default.svg";
import chevronDown from "$images/icons/button/Name=Chevron-down, Size=16, Tone=Default.svg";
import deleteIcon from "$images/icons/button/Name=Delete, Size=16, Tone=Error.svg";
import addIcon from "$images/icons/button/Name=Add, Size=16, Tone=Default.svg";
import GhostButton from "@/elements/UI/button/ButtonStyle/Default/ghost/GhostButton.vue";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";
import GhostButtonError from "@/elements/UI/button/ButtonStyle/Error/ghost/GhostButtonError.vue";

type ActionImagePayload = {
  file: File;
  actionIndex: number;
};

type CachedPreview = {
  url: string;
  signature: string;
};

export default defineComponent({
  name: "ActionSettingComponent",

  components: {
    ButtonIcon,
    GhostButton,
    InputFieldMedia,
    AccordionBase,
    InputStyle
  },

  data() {
    return {
      style,
      addIcon,
      DOMAIN,
      modifyQuiz,
      selectedActionIndex: null as number | null,
      newActionImgList: [] as string[],
      cachedPreviewUrls: {} as Record<string, CachedPreview>
    };
  },

  beforeUnmount() {
    this.revokePreviewUrls();
  },

  computed: {
    quiz(): Quiz {
      return this.$quizStore.getQuiz;
    },

    actionList(): action[] {
      const value = this.modifyQuiz("get", "", ["actionList"]);

      return Array.isArray(value) ? value : [];
    },

    cachedFiles(): File[] {
      const value = this.$quizStore.getCachedFiles;

      return Array.isArray(value) ? value : [];
    },

    mobileVersion(): boolean {
      return this.$quizStore.getSelectedVersion === "Мобильная";
    },

    actionHeader(): string {
      return this.modifyQuiz("get", "", ["actionHeader"]);
    }
  },

  watch: {
    cachedFiles: {
      handler(): void {
        this.cleanupCachedPreviewUrls();
      },
      deep: true
    }
  },

  methods: {
    updateActionHeader(value: string): void {
      this.modifyQuiz("set", value, ["actionHeader"]);
    },

    addAction(): void {
      this.duplicateActionsToABTest();

      const actionList = [...this.actionList];

      actionList.push({
        img: "",
        header: "",
        paragraph: ""
      });

      this.modifyQuiz("set", actionList, ["actionList"]);
    },

    uploadPhotoAction(actionIndex: number): void {
      this.selectedActionIndex = actionIndex;

      const refInput = this.$refs.fileUpload as HTMLInputElement | undefined;

      refInput?.click();
    },

    setActionImage(event: Event): void {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (this.selectedActionIndex === null || !file) {
        this.selectedActionIndex = null;
        target.value = "";
        return;
      }

      this.uploadActionImage({
        file,
        actionIndex: this.selectedActionIndex
      });

      this.selectedActionIndex = null;
      target.value = "";
    },

    uploadActionImage(payload: ActionImagePayload): void {
      if (
        payload.actionIndex < 0 ||
        payload.actionIndex >= this.actionList.length
      ) {
        return;
      }

      this.duplicateActionsToABTest();

      const newFileName = this.getActionImageFileName(
        payload.actionIndex,
        payload.file
      );
      const storedImagePath = this.getActionImageStoredPath(newFileName);
      const blob = payload.file.slice(0, payload.file.size, payload.file.type);
      const newFile = new File([blob], newFileName, {
        type: payload.file.type,
        lastModified: payload.file.lastModified
      });

      const oldAction = this.actionList[payload.actionIndex];
      const oldFileName = this.getFileBaseName(oldAction?.img || "");

      this.removeCachedFileByName(oldFileName);
      this.removeCachedFilesByActionIndex(payload.actionIndex);
      this.revokeActionPreview(payload.actionIndex);

      this.newActionImgList[payload.actionIndex] = URL.createObjectURL(newFile);

      this.markActionImageAsChanged(newFileName);
      this.markActionImageAsChanged(storedImagePath);

      this.modifyQuiz("set", storedImagePath, [
        "actionList",
        payload.actionIndex,
        "img"
      ]);

      this.replaceCachedFile(newFile);
    },

    getActionImage(path: string, actionIndex: number): string {
      const previewUrl = this.newActionImgList[actionIndex];

      if (previewUrl) {
        return `url('${previewUrl}')`;
      }

      if (!path) {
        return "";
      }

      if (path.startsWith("blob:")) {
        return `url('${path}')`;
      }

      if (path.startsWith("http://") || path.startsWith("https://")) {
        return `url('${path}')`;
      }

      const cachedPreviewUrl = this.getCachedFilePreviewUrl(path);

      if (cachedPreviewUrl) {
        return `url('${cachedPreviewUrl}')`;
      }

      return this.getServerImageUrl(path);
    },

    getActionMediaType(path: string): "image" | "video" {
      return this.isVideoPath(path) ? "video" : "image";
    },

    isVideoPath(path = ""): boolean {
      return /\.(mp4|webm|ogg)(?:$|[?#])/i.test(path);
    },

    getCachedFilePreviewUrl(path: string): string {
      const fileName = this.getFileBaseName(path);
      const cachedFile = this.findCachedFileByName(fileName);

      if (!cachedFile) {
        return "";
      }

      const signature = this.getFileSignature(cachedFile);
      const currentPreview = this.cachedPreviewUrls[fileName];

      if (currentPreview?.signature === signature) {
        return currentPreview.url;
      }

      if (currentPreview?.url?.startsWith("blob:")) {
        URL.revokeObjectURL(currentPreview.url);
      }

      const previewUrl = URL.createObjectURL(cachedFile);

      this.cachedPreviewUrls = {
        ...this.cachedPreviewUrls,
        [fileName]: {
          url: previewUrl,
          signature
        }
      };

      return previewUrl;
    },

    getActionActions(actionIndex: number): ListActionItem[] {
      return [
        {
          text: "Переместить наверх",
          icon: chevronUp,
          handler: () => this.swapTop(actionIndex),
          disabled: actionIndex === 0,
          variant: "button"
        },
        {
          text: "Переместить вниз",
          icon: chevronDown,
          handler: () => this.swapDown(actionIndex),
          disabled: actionIndex === this.actionList.length - 1,
          variant: "button"
        },
        {
          text: "Удалить",
          icon: deleteIcon,
          handler: () => this.deleteAction(actionIndex),
          variant: "button",
          buttonComponent: markRaw(GhostButtonError)
        }
      ];
    },

    swapTop(actionIndex: number): void {
      if (actionIndex <= 0) {
        return;
      }

      this.duplicateActionsToABTest();

      const actionList = this.actionList.map((item) => ({...item}));
      const currentAction = actionList[actionIndex];
      const previousAction = actionList[actionIndex - 1];

      actionList[actionIndex - 1] = currentAction;
      actionList[actionIndex] = previousAction;

      const currentPreview = this.newActionImgList[actionIndex];
      const previousPreview = this.newActionImgList[actionIndex - 1];

      this.newActionImgList[actionIndex - 1] = currentPreview;
      this.newActionImgList[actionIndex] = previousPreview;

      this.modifyQuiz("set", this.normalizePendingActionFiles(actionList), [
        "actionList"
      ]);
    },

    swapDown(actionIndex: number): void {
      if (actionIndex >= this.actionList.length - 1) {
        return;
      }

      this.duplicateActionsToABTest();

      const actionList = this.actionList.map((item) => ({...item}));
      const currentAction = actionList[actionIndex];
      const nextAction = actionList[actionIndex + 1];

      actionList[actionIndex + 1] = currentAction;
      actionList[actionIndex] = nextAction;

      const currentPreview = this.newActionImgList[actionIndex];
      const nextPreview = this.newActionImgList[actionIndex + 1];

      this.newActionImgList[actionIndex + 1] = currentPreview;
      this.newActionImgList[actionIndex] = nextPreview;

      this.modifyQuiz("set", this.normalizePendingActionFiles(actionList), [
        "actionList"
      ]);
    },

    deleteAction(actionIndex: number): void {
      if (actionIndex < 0 || actionIndex >= this.actionList.length) {
        return;
      }

      this.duplicateActionsToABTest();

      const actionList = this.actionList.map((item) => ({...item}));
      const deletedAction = actionList[actionIndex];

      this.removeCachedFileByName(
        this.getFileBaseName(deletedAction?.img || "")
      );
      this.removeCachedFilesByActionIndex(actionIndex);
      this.revokeActionPreview(actionIndex);

      actionList.splice(actionIndex, 1);
      this.newActionImgList.splice(actionIndex, 1);

      this.modifyQuiz("set", this.normalizePendingActionFiles(actionList), [
        "actionList"
      ]);
    },

    duplicateActionsToABTest(): void {
      const isABTestNow = localStorage.getItem("ABTest");

      if (!this.quiz?.ABTestBool && !isABTestNow) {
        return;
      }

      const actionList = this.actionList;

      if (!Array.isArray(actionList)) {
        return;
      }

      if (!this.quiz.ABTestObj) {
        this.quiz.ABTestObj = {
          actionList: this.cloneActionList(actionList)
        };

        return;
      }

      const ABTestObj = this.quiz.ABTestObj as {actionList?: action[]};

      if (!ABTestObj.actionList) {
        ABTestObj.actionList = this.cloneActionList(actionList);
      }
    },

    cloneActionList(actionList: action[]): action[] {
      return actionList.map((item) => ({
        img: item.img,
        header: item.header,
        paragraph: item.paragraph
      }));
    },

    getActionKey(actionItem: action, actionIndex: number): string {
      return `${actionIndex}-${actionItem.img || "empty"}`;
    },

    getActionImageFileName(actionIndex: number, file: File): string {
      const fileExtension = this.getFileExtension(file);

      return `${actionIndex}-action.${this.quiz._id}.${fileExtension}`;
    },

    getActionImageStoredPath(fileName: string): string {
      const baseName = this.getFileBaseName(fileName);
      const quiz = this.quiz as Quiz & {userId?: string};
      const userId = String(quiz?.userId || "");

      if (!userId || !baseName) {
        return baseName;
      }

      const pathParts = [userId, "quiz"];

      if (this.mobileVersion) {
        pathParts.push("mobile");
      }

      if (localStorage.getItem("ABTest") === "true") {
        pathParts.push("ABTest");
      }

      pathParts.push(baseName);

      return pathParts.join("/");
    },

    getActionImageVersionStorageKey(path: string): string {
      return `readds-action-image-version:${path}`;
    },

    markActionImageAsChanged(path: string): void {
      const normalizedPath = String(path || "");
      const baseName = this.getFileBaseName(normalizedPath);
      const version = String(Date.now());

      if (normalizedPath) {
        localStorage.setItem(
          this.getActionImageVersionStorageKey(normalizedPath),
          version
        );
      }

      if (baseName) {
        localStorage.setItem(
          this.getActionImageVersionStorageKey(baseName),
          version
        );
      }
    },

    getActionImageVersion(path: string): string {
      const normalizedPath = String(path || "");
      const baseName = this.getFileBaseName(normalizedPath);

      return (
        localStorage.getItem(
          this.getActionImageVersionStorageKey(normalizedPath)
        ) ||
        localStorage.getItem(this.getActionImageVersionStorageKey(baseName)) ||
        ""
      );
    },

    getServerImageUrl(path: string): string {
      const version = this.getActionImageVersion(path);
      const params = new URLSearchParams({name: path});

      if (version) {
        params.set("v", version);
      }

      return `url('https://${this.DOMAIN}/api/img?${params.toString()}')`;
    },

    getFileExtension(file: File): string {
      const extension = file.name.split(".").pop()?.trim().toLowerCase();

      return extension || "png";
    },

    getFileBaseName(path: string): string {
      return (
        String(path || "")
          .split("/")
          .pop() || ""
      );
    },

    getFileSignature(file: File): string {
      return `${file.name}-${file.size}-${file.lastModified}`;
    },

    getActionFileIndex(fileName: string): number | null {
      const baseName = this.getFileBaseName(fileName);
      const match = baseName.match(/^(\d+)-action\./);

      if (!match) {
        return null;
      }

      const index = Number(match[1]);

      return Number.isFinite(index) ? index : null;
    },

    isCurrentQuizActionFileName(fileName: string): boolean {
      const baseName = this.getFileBaseName(fileName);

      return new RegExp(`^\\d+-action\\.${this.quiz._id}\\.`).test(baseName);
    },

    findCachedFileByName(fileName: string): File | undefined {
      const baseName = this.getFileBaseName(fileName);

      return this.cachedFiles.find((file) => file.name === baseName);
    },

    replaceCachedFile(file: File): void {
      for (let index = this.cachedFiles.length - 1; index >= 0; index--) {
        if (this.cachedFiles[index].name === file.name) {
          this.cachedFiles.splice(index, 1);
        }
      }

      this.cachedFiles.push(file);
    },

    removeCachedFileByName(fileName: string): void {
      const baseName = this.getFileBaseName(fileName);

      if (!baseName) {
        return;
      }

      for (let index = this.cachedFiles.length - 1; index >= 0; index--) {
        if (this.cachedFiles[index].name === baseName) {
          this.cachedFiles.splice(index, 1);
        }
      }
    },

    removeCachedFilesByActionIndex(actionIndex: number): void {
      for (let index = this.cachedFiles.length - 1; index >= 0; index--) {
        const file = this.cachedFiles[index];

        if (
          this.isCurrentQuizActionFileName(file.name) &&
          this.getActionFileIndex(file.name) === actionIndex
        ) {
          this.cachedFiles.splice(index, 1);
        }
      }
    },

    revokeActionPreview(actionIndex: number): void {
      const previewUrl = this.newActionImgList[actionIndex];

      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    },

    normalizePendingActionFiles(actionList: action[]): action[] {
      const cachedFilesByName = new Map<string, File>();

      this.cachedFiles.forEach((file) => {
        cachedFilesByName.set(file.name, file);
      });

      const oldFileNamesToRemove = new Set<string>();
      const newFileNamesToRemove = new Set<string>();
      const filesToInsert: File[] = [];

      const normalizedActions = actionList.map((item, actionIndex) => {
        const normalizedAction = {...item};
        const currentFileName = this.getFileBaseName(
          normalizedAction.img || ""
        );

        if (!this.isCurrentQuizActionFileName(currentFileName)) {
          return normalizedAction;
        }

        const cachedFile = cachedFilesByName.get(currentFileName);

        if (!cachedFile) {
          return normalizedAction;
        }

        const newFileName = this.getActionImageFileName(
          actionIndex,
          cachedFile
        );
        const renamedFile = new File([cachedFile], newFileName, {
          type: cachedFile.type,
          lastModified: cachedFile.lastModified
        });

        oldFileNamesToRemove.add(currentFileName);
        newFileNamesToRemove.add(newFileName);
        filesToInsert.push(renamedFile);

        normalizedAction.img = this.getActionImageStoredPath(newFileName);

        return normalizedAction;
      });

      if (filesToInsert.length === 0) {
        return normalizedActions;
      }

      for (let index = this.cachedFiles.length - 1; index >= 0; index--) {
        const cachedFileName = this.cachedFiles[index].name;

        if (
          oldFileNamesToRemove.has(cachedFileName) ||
          newFileNamesToRemove.has(cachedFileName)
        ) {
          this.cachedFiles.splice(index, 1);
        }
      }

      filesToInsert.forEach((file) => {
        this.cachedFiles.push(file);
      });

      return normalizedActions;
    },

    cleanupCachedPreviewUrls(): void {
      const signatureMap = new Map<string, string>();

      this.cachedFiles.forEach((file) => {
        signatureMap.set(file.name, this.getFileSignature(file));
      });

      Object.keys(this.cachedPreviewUrls).forEach((fileName) => {
        const preview = this.cachedPreviewUrls[fileName];
        const signature = signatureMap.get(fileName);

        if (!signature || signature !== preview.signature) {
          if (preview.url?.startsWith("blob:")) {
            URL.revokeObjectURL(preview.url);
          }

          delete this.cachedPreviewUrls[fileName];
        }
      });
    },

    revokePreviewUrls(): void {
      this.newActionImgList.forEach((url) => {
        if (url?.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });

      Object.values(this.cachedPreviewUrls).forEach((preview) => {
        if (preview.url?.startsWith("blob:")) {
          URL.revokeObjectURL(preview.url);
        }
      });

      this.cachedPreviewUrls = {};
    }
  }
});
</script>
